import React, { useState } from 'react';
import { Zap, Car, ArrowLeft } from 'lucide-react';
import { AppView } from '../../types';

interface FuelElectricityCalculatorProps {
  onNavigateToView: (view: AppView) => void;
  defaultTab?: 'fuel' | 'electricity';
}

export const FuelElectricityCalculator: React.FC<FuelElectricityCalculatorProps> = ({ onNavigateToView, defaultTab = 'fuel' }) => {
  const [subTab, setSubTab] = useState<'fuel' | 'electricity'>(defaultTab);

  // Fuel State
  const [distance, setDistance] = useState<string>('500');
  const [distanceUnit, setDistanceUnit] = useState<'km' | 'mi'>('km');
  const [efficiency, setEfficiency] = useState<string>('7.5'); // L/100km or MPG
  const [efficiencyMode, setEfficiencyMode] = useState<'l_100km' | 'mpg_us' | 'mpg_imp'>('l_100km');
  const [fuelPrice, setFuelPrice] = useState<string>('1.65'); // per liter or gallon
  const [isRoundTrip, setIsRoundTrip] = useState<boolean>(true);

  // Electricity State
  const [watts, setWatts] = useState<string>('1500'); // Appliance watts
  const [hoursPerDay, setHoursPerDay] = useState<string>('5');
  const [daysPerMonth, setDaysPerMonth] = useState<string>('30');
  const [kwhRate, setKwhRate] = useState<string>('0.18');

  // Fuel Calculations
  const dist = parseFloat(distance) || 0;
  const eff = parseFloat(efficiency) || 0;
  const price = parseFloat(fuelPrice) || 0;
  const multiplier = isRoundTrip ? 2 : 1;
  const totalDist = dist * multiplier;

  let fuelRequired = 0;
  let totalFuelCost = 0;

  if (efficiencyMode === 'l_100km') {
    fuelRequired = (totalDist / 100) * eff;
    totalFuelCost = fuelRequired * price;
  } else if (efficiencyMode === 'mpg_us') {
    // miles per US gallon
    const gallons = eff > 0 ? totalDist / eff : 0;
    totalFuelCost = gallons * price;
  } else if (efficiencyMode === 'mpg_imp') {
    const gallons = eff > 0 ? totalDist / eff : 0;
    totalFuelCost = gallons * price;
  }

  // Electricity Calculations
  const w = parseFloat(watts) || 0;
  const h = parseFloat(hoursPerDay) || 0;
  const days = parseFloat(daysPerMonth) || 30;
  const rate = parseFloat(kwhRate) || 0;

  const kWhPerDay = (w * h) / 1000;
  const kWhPerMonth = kWhPerDay * days;
  const monthlyCost = kWhPerMonth * rate;
  const annualCost = monthlyCost * 12;

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A] pb-16">
      <div className="bg-white border-b border-[#E2E8F0] px-6 py-6">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => onNavigateToView('everyday-command-center')}
              className="p-2 bg-[#F1F5F9] hover:bg-[#E2E8F0] rounded-xl text-[#0F172A] transition-colors cursor-pointer"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wider text-blue-600">
                YamooPay Household Costs Engine (Prompt 14)
              </div>
              <h1 className="text-2xl font-bold tracking-tight text-[#0F172A]">
                Calculateur de Coût du Carburant & de l'Électricité
              </h1>
            </div>
          </div>
          <button
            onClick={() => onNavigateToView('everyday-command-center')}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-xs font-bold transition-colors shadow-xs cursor-pointer"
          >
            Centre de Commande
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 mt-8">
        <div className="flex border-b border-[#E2E8F0] gap-6 mb-8">
          {[
            { id: 'fuel', label: 'Coût du Carburant & Trajets' },
            { id: 'electricity', label: 'Coût de l\'Électricité & Appareils' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setSubTab(tab.id as any)}
              className={`pb-3 text-xs font-bold border-b-2 transition-colors cursor-pointer ${
                subTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-[#64748B] hover:text-[#0F172A]'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {subTab === 'fuel' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-2 bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-xs">
              <h3 className="text-base font-bold text-[#0F172A] mb-4">Calculateur de Trajet & Carburant</h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-semibold text-[#475569] mb-1">Distance aller</label>
                  <input
                    type="number"
                    value={distance}
                    onChange={(e) => setDistance(e.target.value)}
                    className="w-full bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl px-4 py-2.5 text-sm font-semibold text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#475569] mb-1">Prix du Carburant (par unité)</label>
                  <input
                    type="number"
                    value={fuelPrice}
                    onChange={(e) => setFuelPrice(e.target.value)}
                    className="w-full bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl px-4 py-2.5 text-sm font-semibold text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-xs font-semibold text-[#475569] mb-1">Consommation (ex: 7.5 L/100km)</label>
                  <input
                    type="number"
                    value={efficiency}
                    onChange={(e) => setEfficiency(e.target.value)}
                    className="w-full bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl px-4 py-2.5 text-sm font-semibold text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </div>
                <div className="flex items-center pt-6">
                  <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-[#0F172A]">
                    <input
                      type="checkbox"
                      checked={isRoundTrip}
                      onChange={(e) => setIsRoundTrip(e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span>Inclure le trajet retour (Aller-retour 2x)</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-yellow-900 to-slate-900 text-white rounded-2xl p-6 flex flex-col justify-between shadow-md">
              <div>
                <div className="text-xs font-bold text-yellow-300 uppercase tracking-wider mb-2">Estimation Carburant</div>
                <div className="space-y-3 mt-4">
                  <div>
                    <div className="text-xs text-yellow-200">Distance totale</div>
                    <div className="text-xl font-bold">{totalDist.toLocaleString()} km</div>
                  </div>
                  <div className="pt-3 border-t border-white/10">
                    <div className="text-xs text-yellow-200">Coût Total du Carburant</div>
                    <div className="text-3xl font-extrabold text-yellow-300">${totalFuelCost.toFixed(2)}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {subTab === 'electricity' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-2 bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-xs">
              <h3 className="text-base font-bold text-[#0F172A] mb-4">Calculateur de Consommation Électrique (Appareils)</h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-semibold text-[#475569] mb-1">Puissance de l'appareil (Watts)</label>
                  <input
                    type="number"
                    value={watts}
                    onChange={(e) => setWatts(e.target.value)}
                    className="w-full bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl px-4 py-2.5 text-sm font-semibold text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#475569] mb-1">Heures d'utilisation par jour</label>
                  <input
                    type="number"
                    value={hoursPerDay}
                    onChange={(e) => setHoursPerDay(e.target.value)}
                    className="w-full bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl px-4 py-2.5 text-sm font-semibold text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-xs font-semibold text-[#475569] mb-1">Jours par mois</label>
                  <input
                    type="number"
                    value={daysPerMonth}
                    onChange={(e) => setDaysPerMonth(e.target.value)}
                    className="w-full bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl px-4 py-2.5 text-sm font-semibold text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#475569] mb-1">Tarif Électricité ($ / kWh)</label>
                  <input
                    type="number"
                    value={kwhRate}
                    onChange={(e) => setKwhRate(e.target.value)}
                    className="w-full bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl px-4 py-2.5 text-sm font-semibold text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-emerald-900 to-slate-900 text-white rounded-2xl p-6 flex flex-col justify-between shadow-md">
              <div>
                <div className="text-xs font-bold text-emerald-300 uppercase tracking-wider mb-2">Bilan Énergétique</div>
                <div className="space-y-3 mt-4">
                  <div>
                    <div className="text-xs text-emerald-200">Consommation mensuelle</div>
                    <div className="text-xl font-bold">{kWhPerMonth.toFixed(1)} kWh</div>
                  </div>
                  <div>
                    <div className="text-xs text-emerald-200">Coût Mensuel</div>
                    <div className="text-2xl font-bold text-emerald-300">${monthlyCost.toFixed(2)}</div>
                  </div>
                  <div className="pt-3 border-t border-white/10">
                    <div className="text-xs text-emerald-200">Coût Annuel Estimé</div>
                    <div className="text-3xl font-extrabold text-white">${annualCost.toFixed(2)}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
